import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    Box,
    Chip,
    CircularProgress,
    alpha,
} from '@mui/material';

const InviteUserDialog = ({
    open,
    onClose,
    onSubmit,
    organizations,
    availableRoles,
    getRoleDisplayName,
    colors,
    t,
}) => {
    const [email, setEmail] = useState('');
    const [orgId, setOrgId] = useState('');
    const [roles, setRoles] = useState(['user']);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open) {
            setEmail('');
            setOrgId('');
            setRoles(['user']);
            setError('');
        }
    }, [open]);

    const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).toLowerCase());

    const handleRoleChange = (event) => {
        const newRoles = event.target.value;
        const lastAdded = newRoles.find((r) => !roles.includes(r));

        // Auto-fix incompatible roles
        if (lastAdded === 'user') {
            setRoles(newRoles.filter((r) => r !== 'admin' && r !== 'billing_admin' && r !== 'super_admin'));
        } else if (['admin', 'billing_admin', 'super_admin'].includes(lastAdded) && newRoles.includes('user')) {
            setRoles(newRoles.filter((r) => r !== 'user'));
        } else {
            setRoles(newRoles);
        }
    };

    const handleSubmit = async () => {
        if (!validateEmail(email)) {
            setError(t('invalid_email', 'Please enter a valid email address'));
            return;
        }
        if (!orgId) {
            setError(t('select_organization', 'Please select an organization'));
            return;
        }
        if (!roles.length) {
            setError(t('select_at_least_one_role', 'Please select at least one role'));
            return;
        }

        setLoading(true);
        setError('');
        try {
            await onSubmit({ email, organization_id: orgId, roles });
        } catch (err) {
            setError(err.message || 'Error');
        } finally {
            setLoading(false);
        }
    };

    const getRoleChipColor = (role) => {
        switch (role) {
            case 'super_admin': return { bg: alpha(colors.warning, 0.12), color: colors.warning };
            case 'admin': return { bg: alpha(colors.secondary, 0.12), color: colors.secondary };
            case 'billing_admin': return { bg: alpha(colors.info, 0.12), color: colors.info };
            default: return { bg: colors.backgroundAlt, color: colors.textSecondary };
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    border: `1px solid ${colors.border}`,
                    boxShadow: colors.isDark ? '0 4px 20px rgba(0,0,0,0.5)' : '0 4px 20px rgba(0,0,0,0.1)',
                },
            }}
        >
            <DialogTitle sx={{ pb: 1, pt: 3, px: 3, fontWeight: 600, fontSize: '1.25rem', color: colors.text }}>
                {t('invite_user', 'Invite User')}
            </DialogTitle>
            <DialogContent sx={{ p: 3, pt: 2 }}>
                <DialogContentText sx={{ mb: 3, color: colors.textSecondary }}>
                    {t('admin_invite_description', 'Invite a user to any organization. They will receive an email invitation.')}
                </DialogContentText>

                <TextField
                    autoFocus
                    label={t('email_address', 'Email Address')}
                    type="email"
                    fullWidth
                    variant="outlined"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(''); }}
                    error={!!error}
                    helperText={error}
                    sx={{ mb: 2.5, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                />

                <FormControl fullWidth sx={{ mb: 2.5 }}>
                    <InputLabel>{t('organization', 'Organization')}</InputLabel>
                    <Select
                        value={orgId}
                        label={t('organization', 'Organization')}
                        onChange={(e) => { setOrgId(e.target.value); setError(''); }}
                        sx={{ borderRadius: '8px' }}
                    >
                        {organizations.map((org) => (
                            <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl fullWidth>
                    <InputLabel>{t('roles', 'Roles')}</InputLabel>
                    <Select
                        multiple
                        value={roles}
                        onChange={handleRoleChange}
                        input={<OutlinedInput label={t('roles', 'Roles')} sx={{ borderRadius: '8px' }} />}
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((value) => {
                                    const chipColor = getRoleChipColor(value);
                                    return (
                                        <Chip
                                            key={value}
                                            label={getRoleDisplayName(value)}
                                            sx={{ bgcolor: chipColor.bg, color: chipColor.color, fontWeight: 500 }}
                                        />
                                    );
                                })}
                            </Box>
                        )}
                    >
                        {availableRoles.map((role) => (
                            <MenuItem key={role} value={role}>{getRoleDisplayName(role)}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
                <Button onClick={onClose} sx={{ color: colors.textSecondary, textTransform: 'none', fontWeight: 500 }}>
                    {t('cancel', 'Cancel')}
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disableElevation
                    disabled={!email || !orgId || !roles.length || loading}
                    sx={{
                        bgcolor: colors.primary, color: 'white', textTransform: 'none',
                        fontWeight: 600, px: 3, py: 1, borderRadius: '8px',
                        '&:hover': { bgcolor: colors.primaryDark },
                        '&.Mui-disabled': { bgcolor: alpha(colors.primary, 0.6), color: 'white' },
                    }}
                >
                    {loading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                            {t('sending', 'Sending...')}
                        </Box>
                    ) : (
                        t('send_invitation', 'Send Invitation')
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default InviteUserDialog;
